import { useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";

interface UrlParams {
  [key: string]: string | undefined;
}

type AccessAsNumberOrUndefined = (paramValue: string | undefined) => number | undefined;

export interface UrlParamsService {
  getProjectId: () => number | undefined;
  getJobId: () => number | undefined;
  getUserId: () => number | undefined;
  getSectionId: () => number | undefined;
  getNodeId: () => number | undefined;
  getStructureId: () => number | undefined;
  getDamageId: () => number | undefined;
  getInspectionId: () => number | undefined;
  getProjectPropertyId: () => number | undefined;
  getSewerObjectParentId: () => number | undefined;
  getFileId: () => number | false
}

export default function useUrlParams(): UrlParamsService {
  const navigate = useNavigate();
  const params: UrlParams = useParams();

  const isValidAsNumber = useCallback((paramValue: string | undefined) => !!Number(paramValue), []);

  const accessPropertyAsNumber = useCallback(
    (paramValue: string | undefined) => isValidAsNumber(paramValue) && Number(paramValue),
    [isValidAsNumber]
  );

  const redirectTo404 = useCallback((): undefined => {
    navigate("/404");
    return undefined;
  }, [navigate]);

  const accessPropertyAsNumberOrRedirect: AccessAsNumberOrUndefined = useCallback(
    (paramValue) => accessPropertyAsNumber(paramValue) || redirectTo404(),
    [accessPropertyAsNumber, redirectTo404]
  );

  const accessSewerObjectParentId = useCallback(
    () => {
      if (params.projectId)
        return accessPropertyAsNumberOrRedirect(params.projectId);
      else if (params.jobId)
        return accessPropertyAsNumberOrRedirect(params.jobId);
      return undefined;
    }, [accessPropertyAsNumberOrRedirect, params.jobId, params.projectId]
  );

  const paramsAccessors = useMemo(
    () => ({
      getProjectId: () => accessPropertyAsNumberOrRedirect(params.projectId),
      getJobId: () => accessPropertyAsNumberOrRedirect(params.jobId),
      getUserId: () => accessPropertyAsNumberOrRedirect(params.userId),
      getSectionId: () => accessPropertyAsNumberOrRedirect(params.sectionId),
      getNodeId: () => accessPropertyAsNumberOrRedirect(params.nodeId),
      getStructureId: () => accessPropertyAsNumberOrRedirect(params.structureId),
      getDamageId: () => accessPropertyAsNumberOrRedirect(params.damageId),
      getInspectionId: () => accessPropertyAsNumberOrRedirect(params.inspectionId),
      getProjectPropertyId: () => accessPropertyAsNumberOrRedirect(params.projectPropertyId),
      getSewerObjectParentId: () => accessSewerObjectParentId(),
      getFileId: () => accessPropertyAsNumber(params.fileId)
    }),
    [accessPropertyAsNumberOrRedirect, accessSewerObjectParentId, params.damageId, params.inspectionId, params.jobId, params.nodeId, params.projectId, params.projectPropertyId, params.sectionId, params.structureId, params.userId]
  );

  return paramsAccessors;
}
